// Only structured Timeline context is accepted; never accept an arbitrary return URL.
export function getTimelineNavigationContext(query) {
  const source = ['timeline', 'profile'].includes(query.source) ? query.source : null;
  const periodType = ['year', 'decade'].includes(query.timelineView) ? query.timelineView : null;
  const decade = typeof query.decade === 'string' && query.decade ? query.decade : null;
  return {source, periodType: decade ? 'decade' : periodType, decade};
}

export function getTimelineNavigationQuery(periodType, decade = null) {
  return {
    ...(['year', 'decade'].includes(periodType) ? {timelineView: periodType} : {}),
    ...(decade ? {decade} : {})
  };
}

export function useTimelineNavigation(ownerId, source) {
  const route = useRoute();
  const router = useRouter();
  const snapshots = useState('timeline-navigation-snapshots', () => ({}));
  const key = computed(() => JSON.stringify([useSessionStore().currentUserId, unref(ownerId), source]));
  const navigationState = computed({
    get: () => snapshots.value[key.value] || null,
    set: value => {
      if (value) snapshots.value[key.value] = value;
      else delete snapshots.value[key.value];
    }
  });
  const context = computed(() => getTimelineNavigationContext(route.query));
  const initialPeriodType = computed(() => context.value.periodType || navigationState.value?.periodType || null);
  const selectedDecadeId = computed(() => context.value.decade);

  function location(periodType, decade = null) {
    const query = {...route.query};
    delete query.decade;
    delete query.source;
    return {
      path: route.path,
      query: {...query, ...getTimelineNavigationQuery(periodType, decade)}
    };
  }

  return {
    navigationState,
    initialPeriodType,
    selectedDecadeId,
    replacePeriodView: type => router.replace(location(type)),
    enterDecade: decade => router.push(location('decade', decade)),
    exitDecade: () => navigateTimelineBack(location('decade')),
    yearQuery: state => ({source, ...getTimelineNavigationQuery(state.periodType, state.selectedDecadeId)})
  };
}

export async function navigateTimelineBack(target, event) {
  if (event && (event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return;
  event?.preventDefault();
  const router = useRouter();
  if (process.client && window.history.state?.back === router.resolve(target).fullPath) {
    router.back();
    return;
  }
  await router.replace(target);
}

export function useTimelineNavigationRestoration({pending, browser, timeline, navigationState, active = true}) {
  const route = useRoute();
  let generation = 0;
  let leaving = false;
  let arrivalState;
  onBeforeRouteLeave(() => {
    leaving = true;
    generation++;
  });
  const removeAfterEach = useRouter().afterEach((to, from, failure) => {
    if (failure) leaving = false;
  });
  onScopeDispose(() => {
    generation++;
    removeAfterEach();
  });

  // Snapshot writes belong to departure, not restoration on this same instance.
  watch([pending, browser, () => Boolean(unref(active))], async (values, oldValues, onCleanup) => {
    const attempt = ++generation;
    onCleanup(() => { generation++; });
    if (!process.client || pending.value || !browser.value || !unref(active) || leaving) return;
    if (arrivalState === undefined) arrivalState = navigationState.value;
    const state = arrivalState;
    if (!state || navigationState.value !== state) return;
    const context = getTimelineNavigationContext(route.query);
    if (state.timelineId !== timeline.value?.id
      || (context.periodType && (context.periodType !== state.periodType || context.decade !== state.selectedDecadeId))) {
      navigationState.value = null;
      return;
    }
    const targetBrowser = browser.value;
    const isCurrent = () => {
      const currentContext = getTimelineNavigationContext(route.query);
      return attempt === generation && !leaving && !pending.value && Boolean(unref(active))
        && browser.value === targetBrowser && navigationState.value === state
        && timeline.value?.id === state.timelineId
        && currentContext.periodType === context.periodType && currentContext.decade === context.decade;
    };
    await nextTick();
    if (!isCurrent()) return;
    const restored = await targetBrowser.restoreNavigationState(state, {isCurrent});
    if (restored && isCurrent()) navigationState.value = null;
  }, {immediate: true});
}
